import _extends from '@babel/runtime/helpers/extends';
import React__default, { useRef, useState, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { M as Modal } from '../Modal/Modal-BWQMUu5q.js';
import { g as getTranslationComponent } from '../i18n/ChatBoxInputTranslation/index-BmD8Abdb.js';
import { F as Flex } from '../Flex/Flex-77mGg7gI.js';
import { i as isMobile } from '../theme/style-mixins/isMobile/isMobile-CGsYoba7.js';
import { I as IntlProvider } from '../LocalModules/storm-ui-intl/dist/esm/IntlProvider-DUNZ897o-Dn-1_05J.js';
import { u as useIntl } from '../LocalModules/storm-ui-intl/dist/esm/3rdPartyModules/react-intl/lib/src/components/useIntl-voJ4yxcS-DU7lWLPF.js';
import { u as useTaktId } from '../TaktIdContext/TaktIdContext-C6vdL-6Z.js';
import { C as CloseButton } from '../Button/CloseButton/CloseButton-B_qumqxY.js';
import { K as KEYBOARD_KEYNAMES } from '../LocalModules/storm-ui-utils/dist/esm/keyboardKeynames-CVa_vjg3-CVa_vjg3.js';
import { T as Text } from '../Text/Text-CO4HftGt.js';

const Pill = /*#__PURE__*/styled(Flex).withConfig({
  componentId: "sc-storm-ui-40026394__sc-ipxb9j-0"
})(["border:", ";border-radius:", ";background-color:", ";font-size:", ";min-width:0;max-width:100%;:hover{border:", ";background-color:", ";}", " ", ""], ({
  theme
}) => theme.border.subtle.default, ({
  theme
}) => theme.border.radius.small, ({
  theme
}) => theme.surface.subtleOffset, ({
  theme
}) => theme.font.text.xsmall.default, ({
  theme
}) => theme.border.subtle.hover, ({
  theme
}) => theme.surface.offset, ({
  $disabled
}) => $disabled && css(["border:", ";background-color:", ";"], ({
  theme
}) => theme.border.subtle.default, ({
  theme
}) => theme.surface.offset), ({
  $isImage
}) => !$isImage && css(["padding-inline-start:", ";", ""], ({
  theme
}) => theme.controls.paddingInline.medium.default, isMobile(css(["padding-inline-start:", ";"], ({
  theme
}) => theme.controls.paddingInline.medium.mobile))));
const FileTextWrapper = /*#__PURE__*/styled(Flex).withConfig({
  componentId: "sc-storm-ui-40026394__sc-ipxb9j-1"
})(["min-width:0;"]);
const FileText = /*#__PURE__*/styled(Text).withConfig({
  componentId: "sc-storm-ui-40026394__sc-ipxb9j-2"
})(["", " width:100%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0;"], ({
  $viewOnly
}) => !$viewOnly && css(["max-width:100px;"]));
const LargeImage = /*#__PURE__*/styled('img').withConfig({
  componentId: "sc-storm-ui-40026394__sc-ipxb9j-3"
})(["max-width:100%;height:auto;"]);
const Thumbnail = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-40026394__sc-ipxb9j-4"
})(["display:flex;"]);
const SmallImage = /*#__PURE__*/styled('img').withConfig({
  componentId: "sc-storm-ui-40026394__sc-ipxb9j-5"
})(["white-space:nowrap;border-radius:", ";height:", ";width:", ";cursor:pointer;box-shadow:", ";"], ({
  theme
}) => theme.chatBoxInput.attachment.image.borderRadius, ({
  theme
}) => theme.chatBoxInput.attachment.image.height, ({
  theme
}) => theme.chatBoxInput.attachment.image.width, ({
  theme
}) => theme.chatBoxInput.attachment.image.innerShadow);
const AttachmentPillInner = ({
  attachment,
  onClose,
  disabled,
  viewOnly = false
}) => {
  const {
    formatMessage
  } = useIntl();
  const thumbnailRef = useRef(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const toggleModal = () => {
    setModalOpen(!isModalOpen);
  };
  const handleKeyDownImage = event => {
    if (event.key === KEYBOARD_KEYNAMES.ENTER) {
      toggleModal();
    }
  };
  const handleKeyDownIcon = event => {
    if (event.key === KEYBOARD_KEYNAMES.ENTER) {
      onClose === null || onClose === void 0 || onClose();
    }
  };
  const isImage = attachment.type.split('/').includes('image');
  const getImgUrl = useMemo(() => URL.createObjectURL(attachment), [attachment]);
  const splitFileName = attachment.name.split('.');
  const fileName = splitFileName.slice(0, splitFileName.length - 1).join('.');
  const fileExtension = splitFileName.pop();
  const {
    getDataTaktAttributes
  } = useTaktId({
    fallbackId: 'close-button'
  });
  return /*#__PURE__*/React__default.createElement(Pill, {
    $disabled: disabled,
    $viewOnly: viewOnly,
    $isImage: isImage,
    "data-testid": "attachment-pill",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "xxsmall",
    padding: "xxxsmall"
  }, isImage ? /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Modal, {
    isOpen: isModalOpen,
    onClose: toggleModal,
    toggleEl: thumbnailRef.current,
    padding: "xxxsmall"
  }, /*#__PURE__*/React__default.createElement(LargeImage, {
    alt: attachment.name,
    src: URL.createObjectURL(attachment)
  })), /*#__PURE__*/React__default.createElement(Thumbnail, {
    "aria-haspopup": "true",
    ref: thumbnailRef,
    tabIndex: 0,
    role: "button",
    onKeyDown: handleKeyDownImage,
    onClick: toggleModal
  }, /*#__PURE__*/React__default.createElement(SmallImage, {
    alt: formatMessage({
      id: 'thumbnail-alt'
    }, {
      attachment: attachment.name
    }),
    src: getImgUrl
  }))) : /*#__PURE__*/React__default.createElement(FileTextWrapper, {
    direction: "column"
  }, /*#__PURE__*/React__default.createElement(FileText, {
    renderAs: "p",
    $viewOnly: viewOnly
  }, fileName), /*#__PURE__*/React__default.createElement(FileText, {
    renderAs: "p",
    size: "xsmall",
    color: "tertiary",
    $viewOnly: viewOnly
  }, fileExtension)), !viewOnly && /*#__PURE__*/React__default.createElement(CloseButton, _extends({}, getDataTaktAttributes(), {
    id: attachment.name,
    variant: "transparent",
    tabIndex: 0,
    onClick: onClose,
    onKeyDown: handleKeyDownIcon,
    label: formatMessage({
      id: 'remove-attachment'
    }, {
      attachment: attachment.name
    })
  })));
};
const AttachmentPill = ({
  locale,
  ...rest
}) => {
  const translations = getTranslationComponent(locale);
  return /*#__PURE__*/React__default.createElement(IntlProvider, {
    translations: translations
  }, /*#__PURE__*/React__default.createElement(AttachmentPillInner, rest));
};

export { AttachmentPill as A };
//# sourceMappingURL=AttachmentPill-DuloRzCp.js.map
